/* ═══════════════════════════════════════════════
   VANADRI THEME JS — CINEMATIC GSAP ORCHESTRATION
   ═══════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {

  /* ──────────────────────────────────────────
     0. STICKY NAV — scroll-aware frosted glass
     ────────────────────────────────────────── */
  const nav = document.querySelector('nav');
  if (nav) {
    const onScroll = () => {
      if (window.scrollY > 60) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on load
  }

  /* ──────────────────────────────────────────
     1. LENIS SMOOTH SCROLL
     ────────────────────────────────────────── */
  let lenis;
  if (typeof Lenis !== 'undefined') {
    lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 0.9,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    }
  }

  /* ──────────────────────────────────────────
     2. GSAP SETUP
     ────────────────────────────────────────── */
  const hasGSAP = typeof gsap !== 'undefined';
  const hasST   = hasGSAP && typeof ScrollTrigger !== 'undefined';

  if (!hasGSAP || !hasST) {
    // Fallback scroll reveal
    const reveals = document.querySelectorAll('.reveal');
    if (reveals.length > 0 && typeof IntersectionObserver !== 'undefined') {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }});
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
      reveals.forEach(el => obs.observe(el));
    }
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ── CUSTOM EASES ── */
  gsap.registerEase('vanadriEase', function(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  });

  /* ──────────────────────────────────────────
     3. SPLIT TEXT UTILITY
     Splits .vnd-split elements into word/char spans
     ────────────────────────────────────────── */
  function splitIntoWords(el) {
    const text = el.textContent;
    const words = text.split(' ');
    el.innerHTML = words.map(w => `<span class="vnd-word"><span class="vnd-word-inner">${w}</span></span>`).join(' ');
    return el.querySelectorAll('.vnd-word-inner');
  }

  function splitIntoChars(el) {
    const text = el.textContent;
    el.innerHTML = [...text].map(c => c === ' '
      ? ' '
      : `<span class="vnd-char">${c}</span>`
    ).join('');
    return el.querySelectorAll('.vnd-char');
  }

  function splitIntoWordChars(el) {
    const words = el.textContent.trim().split(/\s+/);
    el.innerHTML = words.map(word => {
      const chars = [...word].map(c => `<span class="vnd-char">${c}</span>`).join('');
      return `<span class="vnd-word"><span class="vnd-word-inner">${chars}</span></span>`;
    }).join(' ');
    return el.querySelectorAll('.vnd-char');
  }

  /* ──────────────────────────────────────────
     4. HERO — CINEMATIC PINNED PARALLAX
     ────────────────────────────────────────── */
  const hero = document.querySelector('.hero');
  const heroBg = document.querySelector('.hero-fullbleed-img');

  if (hero && heroBg) {
    // Background moves at 40% scroll speed (deep parallax)
    gsap.to(heroBg, {
      yPercent: 30,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      }
    });

    // Hero content fades and drifts up faster
    const heroContent = hero.querySelectorAll('.hero-tagline, .hero-title, .hero-brand-divider, .hero-desc, .hero-cta-group, .hero-stat-badge');
    if (heroContent.length > 0) {
      gsap.to(heroContent, {
        yPercent: -20,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '60% top',
          scrub: 1,
        }
      });
    }
  }

  // Hero stat badge float
  const statBadge = document.querySelector('.hero-stat-badge');
  if (statBadge) {
    gsap.fromTo(statBadge,
      { opacity: 0, x: 20 },
      { opacity: 1, x: 0, duration: 1.4, delay: 1.8, ease: 'power3.out' }
    );
    // Floating loop
    gsap.to(statBadge, {
      y: -8,
      duration: 3,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    });
  }

  /* ──────────────────────────────────────────
     5. CINEMATIC PROCESS SECTION
     Horizontally pinned scroll journey
     ────────────────────────────────────────── */
  const processTrack = document.querySelector('.vnd-process-track');
  const processPanels = document.querySelectorAll('.vnd-process-panel');

  if (processTrack && processPanels.length > 0) {
    const totalWidth = processTrack.scrollWidth - window.innerWidth;

    gsap.to(processTrack, {
      x: -totalWidth,
      ease: 'none',
      scrollTrigger: {
        trigger: '.vnd-process-pin-container',
        pin: true,
        scrub: 1,
        start: 'top top',
        end: () => `+=${totalWidth + window.innerHeight}`,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      }
    });

    // Reveal each panel content on scroll entry
    processPanels.forEach((panel, i) => {
      const title = panel.querySelector('.vnd-process-panel__title');
      const desc  = panel.querySelector('.vnd-process-panel__desc');
      const detail = panel.querySelector('.vnd-process-panel__detail');
      const num   = panel.querySelector('.vnd-process-panel__num');

      if (title) {
        const words = splitIntoWords(title);
        gsap.fromTo(words,
          { yPercent: 110, opacity: 0 },
          {
            yPercent: 0, opacity: 1,
            duration: 1.2,
            stagger: 0.06,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: panel,
              containerAnimation: gsap.getById(`process-scroll-${i}`),
              start: 'left 70%',
              toggleActions: 'play none none none',
            }
          }
        );
      }

      if (num) {
        gsap.fromTo(num,
          { scale: 1.4, opacity: 0 },
          {
            scale: 1, opacity: 1,
            duration: 1.6,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: panel,
              start: 'left center',
              containerAnimation: gsap.getById(`process-scroll-${i}`),
              toggleActions: 'play none none none',
            }
          }
        );
      }
    });
  }

  /* ──────────────────────────────────────────
     6. ORIGIN SECTION — CINEMATIC PARALLAX
     ────────────────────────────────────────── */
  const originWrapper = document.querySelector('.vanadri-origin-wrapper');
  const originImg = document.querySelector('.vanadri-origin-visual .vanadri-frame');
  const originText = document.querySelector('.vanadri-origin-text');

  if (originWrapper && originImg) {
    // Image rises as you scroll into it
    gsap.fromTo(originImg,
      { yPercent: 12, opacity: 0, scale: 0.96 },
      {
        yPercent: 0, opacity: 1, scale: 1,
        duration: 1.6,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: originWrapper,
          start: 'top 70%',
          toggleActions: 'play none none none',
        }
      }
    );

    // Continuing parallax while scrolling through
    gsap.to(originImg, {
      yPercent: -8,
      ease: 'none',
      scrollTrigger: {
        trigger: originWrapper,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 2,
      }
    });
  }

  if (originText) {
    const heading = originText.querySelector('.vanadri-origin-title');
    if (heading) {
      const words = splitIntoWords(heading);
      gsap.fromTo(words,
        { yPercent: 100, opacity: 0 },
        {
          yPercent: 0, opacity: 1,
          duration: 1.1,
          stagger: 0.08,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: originWrapper,
            start: 'top 65%',
            toggleActions: 'play none none none',
          }
        }
      );
    }

    const body = originText.querySelectorAll('.vanadri-origin-content p, .btn-cream-outline');
    gsap.fromTo(body,
      { x: 40, opacity: 0 },
      {
        x: 0, opacity: 1,
        duration: 1.0,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: originWrapper,
          start: 'top 60%',
          toggleActions: 'play none none none',
        }
      }
    );
  }

  /* ──────────────────────────────────────────
     7. ROBUSTA SECTION — DRAMATIC ENTRANCE
     ────────────────────────────────────────── */
  const robustaWrapper = document.querySelector('.vanadri-robusta-wrapper');
  const robustaImg = document.querySelector('.vanadri-robusta-visual .vanadri-frame');
  const robustaTitle = document.querySelector('.vanadri-robusta-title');

  if (robustaWrapper) {
    // Background color wash on entry
    gsap.fromTo(robustaWrapper,
      { backgroundImage: 'linear-gradient(135deg, #431020 0%, #2a0b15 100%)' },
      {
        backgroundImage: 'linear-gradient(135deg, #431020 0%, #431020 100%)',
        ease: 'none',
        scrollTrigger: {
          trigger: robustaWrapper,
          start: 'top bottom',
          end: 'top top',
          scrub: true,
        }
      }
    );

    if (robustaImg) {
      gsap.fromTo(robustaImg,
        { rotationZ: -5, scale: 0.93, opacity: 0 },
        {
          rotationZ: 0, scale: 1, opacity: 1,
          duration: 1.8,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: robustaWrapper,
            start: 'top 70%',
            toggleActions: 'play none none none',
          }
        }
      );

      // Continuous slow float
      gsap.to(robustaImg, {
        y: -12,
        duration: 4,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    }

    if (robustaTitle) {
      const chars = splitIntoWordChars(robustaTitle);
      gsap.fromTo(chars,
        { opacity: 0, y: 30, rotationX: 90 },
        {
          opacity: 1, y: 0, rotationX: 0,
          duration: 0.8,
          stagger: 0.025,
          ease: 'back.out(1.7)',
          scrollTrigger: {
            trigger: robustaWrapper,
            start: 'top 65%',
            toggleActions: 'play none none none',
          }
        }
      );
    }
  }

  /* ──────────────────────────────────────────
     8. TRUST BAR — STAGGER ENTRANCE
     ────────────────────────────────────────── */
  const trustItems = document.querySelectorAll('.vnd-trust-item');
  if (trustItems.length > 0) {
    gsap.fromTo(trustItems,
      { y: 20, opacity: 0, scale: 0.96 },
      {
        y: 0, opacity: 1, scale: 1,
        duration: 0.7,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.vnd-trust-bar',
          start: 'top 88%',
          toggleActions: 'play none none none',
        }
      }
    );
  }

  /* ──────────────────────────────────────────
     9. SUBSCRIPTION TIERS — STAGGER + SCALE
     ────────────────────────────────────────── */
  const subTiers = document.querySelectorAll('.vnd-tier-card');
  if (subTiers.length > 0) {
    gsap.fromTo(subTiers,
      { y: 50, opacity: 0, scale: 0.94 },
      {
        y: 0, opacity: 1, scale: 1,
        duration: 1.0,
        stagger: 0.18,
        ease: 'back.out(1.4)',
        scrollTrigger: {
          trigger: '.vanadri-discovery-wrapper',
          start: 'top 72%',
          toggleActions: 'play none none none',
        }
      }
    );
  }

  /* ──────────────────────────────────────────
     10. VALUES SECTION — SLIDE IN
     ────────────────────────────────────────── */
  const valueItems = document.querySelectorAll('.value-item');
  if (valueItems.length > 0) {
    gsap.fromTo(valueItems,
      { x: -30, opacity: 0 },
      {
        x: 0, opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.values-section',
          start: 'top 72%',
          toggleActions: 'play none none none',
        }
      }
    );
  }

  /* ──────────────────────────────────────────
     11. EARLY ACCESS SECTION
     ────────────────────────────────────────── */
  const eaSection = document.querySelector('.vnd-early-access');
  if (eaSection) {
    const eaTitle = eaSection.querySelector('.vnd-ea-title');
    if (eaTitle) {
      const words = splitIntoWords(eaTitle);
      gsap.fromTo(words,
        { yPercent: 110, opacity: 0 },
        {
          yPercent: 0, opacity: 1,
          duration: 1.2,
          stagger: 0.07,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: eaSection,
            start: 'top 70%',
            toggleActions: 'play none none none',
          }
        }
      );
    }
  }

  /* ──────────────────────────────────────────
     12. GENERAL SCROLL REVEAL FALLBACK
     ────────────────────────────────────────── */
  const reveals = document.querySelectorAll('.reveal');
  if (reveals.length > 0 && typeof IntersectionObserver !== 'undefined') {
    const revealObs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          revealObs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(el => revealObs.observe(el));
  }

  /* ──────────────────────────────────────────
     13. STAT COUNTER ANIMATION
     ────────────────────────────────────────── */
  const counters = document.querySelectorAll('[data-count]');
  if (counters.length > 0) {
    const countObs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.dataset.counted) {
          entry.target.dataset.counted = 'true';
          const target = parseFloat(entry.target.dataset.count);
          const duration = 2000;
          const start = performance.now();
          const isFloat = String(entry.target.dataset.count).includes('.');

          function update(now) {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4);
            const current = target * eased;
            entry.target.textContent = isFloat
              ? current.toFixed(1)
              : Math.floor(current);
            if (progress < 1) requestAnimationFrame(update);
            else entry.target.textContent = isFloat ? target.toFixed(1) : Math.floor(target);
          }
          requestAnimationFrame(update);
        }
      });
    }, { threshold: 0.5 });
    counters.forEach(c => countObs.observe(c));
  }

  /* ──────────────────────────────────────────
     14. ROBUSTA FAQ ACCORDION
     ────────────────────────────────────────── */
  const faqItems = document.querySelectorAll('.vnd-faq-item');
  faqItems.forEach(item => {
    const trigger = item.querySelector('.vnd-faq-trigger');
    const answer  = item.querySelector('.vnd-faq-answer');
    if (!trigger || !answer) return;

    trigger.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');

      document.querySelectorAll('.vnd-faq-item.open').forEach(openItem => {
        openItem.classList.remove('open');
        const a = openItem.querySelector('.vnd-faq-answer');
        if (a) a.style.maxHeight = '0';
      });

      if (!isOpen) {
        item.classList.add('open');
        answer.style.maxHeight = answer.scrollHeight + 'px';
      }
    });
  });

  /* ──────────────────────────────────────────
     15. CTA MAGNETIC HOVER
     ────────────────────────────────────────── */
  const magnetBtns = document.querySelectorAll('.btn-primary, .btn-ghost, .btn-cream-outline');
  magnetBtns.forEach(btn => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
      gsap.to(btn, { x, y, duration: 0.4, ease: 'power2.out' });
    });
    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, { x: 0, y: 0, duration: 0.6, ease: 'elastic.out(1, 0.5)' });
    });
  });

  /* ──────────────────────────────────────────
     16. SUBSCRIPTION TIER SELECTION
     ────────────────────────────────────────── */
  const tierCards = document.querySelectorAll('.vnd-tier-card');
  tierCards.forEach(card => {
    card.addEventListener('click', () => {
      tierCards.forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
      gsap.fromTo(card, { scale: 0.97 }, { scale: 1, duration: 0.4, ease: 'back.out(2)' });
    });
  });

});
